<template>
  <settings-group
    title="Consensus Settings"
    description="Control how many and which reviewers are required for consensus and who can resolve conflicts"
  >
    <div v-if="disabled">
      Only lead reviewers may modify the consensus settings for the
      review. Please contact your lead reviewer,
      <a :href="leadMailToLink">{{
        review.leadReviewer.researcher.name
      }}</a
      >.
    </div>
    <template v-else>
      <tabs v-model="stage">
        <tab-item
          v-for="tab in tabs"
          :key="tab.value"
          class="text-none"
          :value="tab.value"
        >
          {{ tab.label }}
        </tab-item>
      </tabs>
      <consensus-required-votes-setting
        :key="`${stage}-${updateKey}`"
        v-model="requiredVotesModel"
      />
      <v-divider />
      <consensus-trusted-reviewers-setting
        :key="`${stage}-trusted-reviewers`"
        :stage="stage"
        :review="review"
        @assign-permission="$emit('assign-permission', $event)"
        @unassign-permission="$emit('unassign-permission', $event)"
        @unassign-all="$emit('unassign-all', $event)"
      />
      <v-divider />
      <consensus-conflict-resolvers-setting
        :key="`${stage}-conflict-resolvers`"
        :stage="stage"
        :review="review"
        @assign-permission="$emit('assign-permission', $event)"
        @unassign-permission="$emit('unassign-permission', $event)"
        @unassign-all="$emit('unassign-all', $event)"
      />
      <required-vote-change-modal
        v-model="showRequiredVoteChangeModal"
        :change-to-many="changingToManyVotes"
        @confirm="onConfirmRequiredVoteChange"
      />
    </template>
  </settings-group>
</template>

<script>
import Tabs from '@/components/Common/Tabs';
import TabItem from '@/components/Common/TabItem';
import SettingsGroup from './SettingsGroup';
import ConsensusRequiredVotesSetting from './ConsensusRequiredVotesSetting';
import ConsensusTrustedReviewersSetting from './ConsensusTrustedReviewersSetting';
import ConsensusConflictResolversSetting from './ConsensusConflictResolversSetting';
import RequiredVoteChangeModal from './RequiredVoteChangeModal';
import { Review, Researcher } from '@/models';
import { ReviewStage } from '@/enums';

const STAGE_TO_PROPS = {
  [ReviewStage.SCREENING]: {
    tab: 'Screening',
    votesRequiredProp: 'screeningVotesRequired',
    updateVotesRequiredEvent: 'update-screening-votes-required',
    progressSummaryProp: 'screeningProgressSummary',
  },
  [ReviewStage.FULL_TEXT_REVIEW]: {
    tab: 'Full Text Review',
    votesRequiredProp: 'fullTextReviewVotesRequired',
    updateVotesRequiredEvent:
      'update-full-text-review-votes-required',
    progressSummaryProp: 'fullTextReviewScreeningProgressSummary',
  },
  [ReviewStage.DATA_EXTRACTION]: {
    tab: 'Data Extraction',
    votesRequiredProp: 'dataExtractionReportsRequired',
    updateVotesRequiredEvent:
      'update-data-extraction-reports-required',
  },
};

export default {
  name: 'ConsensusSettingsPanel',
  components: {
    Tabs,
    TabItem,
    SettingsGroup,
    ConsensusRequiredVotesSetting,
    ConsensusTrustedReviewersSetting,
    ConsensusConflictResolversSetting,
    RequiredVoteChangeModal,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
  },
  emits: [
    'update-screening-votes-required',
    'update-full-text-review-votes-required',
    'update-data-extraction-reports-required',
    'assign-permission',
    'unassign-permission',
    'unassign-all',
  ],
  data: () => ({
    stage: ReviewStage.SCREENING,
    showRequiredVoteChangeModal: false,
    changeRequiredVoteTo: null,
    updateKey: 0,
  }),
  computed: {
    disabled() {
      if (this.currentResearcher.isSuperAdmin) {
        return false;
      }
      return (
        this.review.leadReviewer.researcher.id !==
        this.currentResearcher.id
      );
    },
    leadMailToLink() {
      return `mailto:${this.review.leadReviewer.researcher.email}`;
    },
    tabs() {
      return Object.entries(
        STAGE_TO_PROPS
      ).map(([value, { tab }]) => ({ label: tab, value }));
    },
    requiredVotesModel: {
      get: function () {
        return this.review[this.votesRequiredProp];
      },
      set: function (value) {
        const progressSummary = this.review[this.progressSummaryProp];
        if (progressSummary) {
          const screenedCount = progressSummary.screenedCount;
          const partialCount = progressSummary.partiallyScreenedCount;
          const shouldShowStrategies =
            value > 1 ? screenedCount > 0 : partialCount > 0;

          if (shouldShowStrategies) {
            this.updateKey++;
            this.showRequiredVoteChangeModal = true;
            this.changeRequiredVoteTo = value;
            return;
          }
        }

        this.$emit(this.updateVotesRequiredEvent, {
          changeRequiredVoteTo: value,
        });
      },
    },
    votesRequiredProp() {
      return STAGE_TO_PROPS[this.stage].votesRequiredProp;
    },
    updateVotesRequiredEvent() {
      return STAGE_TO_PROPS[this.stage].updateVotesRequiredEvent;
    },
    progressSummaryProp() {
      return STAGE_TO_PROPS[this.stage].progressSummaryProp;
    },
    changingToManyVotes() {
      return this.changeRequiredVoteTo > 1;
    },
  },
  methods: {
    onConfirmRequiredVoteChange(strategy) {
      this.showRequiredVoteChangeModal = false;
      this.$emit(this.updateVotesRequiredEvent, {
        changeRequiredVoteTo: this.changeRequiredVoteTo,
        strategy,
      });
    },
  },
};
</script>
